import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ChartLineUp, 
  ClockCounterClockwise, 
  Lightning, 
  Target, 
  Warning,
  CheckCircle,
  XCircle,
  ArrowRight,
  Brain,
  TrendUp,
  ChartBar,
  Notebook
} from 'phosphor-react';
import { updateSeoMetadata, getArticleAlternates } from '../lib/seo';

// Logo URL (same as Landing page)
const LOGO_HORIZONTAL = "https://customer-assets.emergentagent.com/job_chart-analyzer-107/artifacts/r9xkm2vj_cerebro%20%2B%20genilateral.png";

const AutomatedTradingJournal = () => {
  useEffect(() => {
    updateSeoMetadata({
      title: 'What Is an Automated Trading Journal? | Complete Guide 2026',
      description: 'Learn what an automated trading journal is and why every trader needs one in 2026. Discover how automation eliminates manual tracking errors and improves trading performance.',
      path: '/automated-trading-journal',
      lang: 'en',
      alternates: getArticleAlternates('/automated-trading-journal'),
    });
    
    return () => {
      // Reset on unmount
      document.title = 'GENI — Turn trading data into better decisions';
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      {/* Navigation */}
      <nav className="border-b border-white/10 bg-[#0A0A0A]/95 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img 
              src={LOGO_HORIZONTAL}
              alt="GENI" 
              className="h-10 md:h-11 w-auto object-contain"
            />
          </Link>
          <Link 
            to="/signup"
            className="px-4 py-2 bg-[#00E676] text-black font-semibold rounded-sm hover:bg-[#00E676]/90 transition-colors text-sm"
          >
            Try GENI Free
          </Link>
        </div>
      </nav>

      {/* Hero Section */}
      <header className="max-w-4xl mx-auto px-6 pt-16 pb-12">
        <div className="flex items-center gap-2 text-[#00E676] text-sm font-medium mb-4">
          <Notebook size={16} weight="fill" />
          <span>Trading Education</span>
        </div>
        
        <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight mb-6">
          What Is an Automated Trading Journal?
          <span className="block text-white/60 text-2xl md:text-3xl mt-2">
            (And Why You Need One in 2026)
          </span>
        </h1>
        
        <p className="text-lg text-white/70 leading-relaxed max-w-3xl">
          An <strong className="text-white">automated trading journal</strong> is software that 
          automatically records, organizes, and analyzes your trades—eliminating manual data entry 
          and giving you instant insights into your trading performance.
        </p>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 pb-20">
        
        {/* Definition Section */}
        <section className="mb-16">
          <div className="bg-gradient-to-br from-[#00E676]/5 to-transparent border border-[#00E676]/20 rounded-lg p-8">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-3">
              <Brain size={24} weight="duotone" className="text-[#00E676]" />
              The Simple Explanation
            </h2>
            <p className="text-white/80 leading-relaxed">
              Think of an <strong className="text-white">automated trading journal</strong> as your 
              personal trading analyst that never sleeps. Instead of manually logging every trade, 
              entry price, exit, and outcome—the software captures this data automatically. 
              It then transforms raw numbers into actionable insights: your win rate by setup, 
              your best-performing timeframes, and patterns you might never notice on your own.
            </p>
          </div>
        </section>

        {/* The Problem Section */}
        <section className="mb-16">
          <h2 className="text-2xl md:text-3xl font-bold mb-8 flex items-center gap-3">
            <Warning size={28} weight="duotone" className="text-red-400" />
            What Most Traders Get Wrong
          </h2>
          
          <p className="text-white/70 mb-8 leading-relaxed">
            Most traders know they should journal. Few actually do it consistently. 
            Here's why manual journaling fails:
          </p>
          
          <div className="grid md:grid-cols-2 gap-6">
            {[
              {
                title: "Time-Consuming Data Entry",
                description: "Manually logging each trade takes 5-10 minutes. Multiply that by 10+ trades per day, and you've lost an hour of trading time.",
                icon: ClockCounterClockwise
              },
              {
                title: "Inconsistent Tracking",
                description: "After a losing day, the last thing you want to do is document your mistakes. This creates gaps in your data.",
                icon: XCircle
              },
              {
                title: "Human Error",
                description: "Wrong entry prices, forgotten stop losses, miscalculated P&L. Small errors compound into unreliable data.",
                icon: Warning
              },
              {
                title: "No Real Analysis",
                description: "Even with perfect data, manually calculating win rates, profit factors, and setup performance is nearly impossible.",
                icon: ChartBar
              }
            ].map((item, index) => (
              <div 
                key={index}
                className="bg-[#121212] border border-white/10 rounded-lg p-6 hover:border-red-400/30 transition-colors"
              >
                <item.icon size={24} weight="duotone" className="text-red-400 mb-3" />
                <h3 className="font-semibold mb-2">{item.title}</h3>
                <p className="text-white/60 text-sm leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
          
          <p className="text-white/70 mt-8 leading-relaxed">
            The result? Traders either abandon journaling entirely or work with incomplete, 
            inaccurate data that leads to wrong conclusions about their performance. This is 
            one of the key reasons <Link to="/why-traders-fail-to-improve" className="text-[#00E676] hover:underline">why traders fail to improve</Link> despite 
            years of experience.
          </p>
        </section>

        {/* The Solution Section */}
        <section className="mb-16">
          <h2 className="text-2xl md:text-3xl font-bold mb-8 flex items-center gap-3">
            <Lightning size={28} weight="duotone" className="text-[#00E676]" />
            What Automation Fixes
          </h2>
          
          <p className="text-white/70 mb-8 leading-relaxed">
            An <strong className="text-white">automated trading journal</strong> solves 
            every problem listed above—and adds capabilities that manual journaling can't match:
          </p>
          
          <div className="space-y-6">
            {[
              {
                title: "100% Data Accuracy",
                description: "Automated capture means no typos, no forgotten fields, no miscalculations. Your data is always reliable.",
                icon: Target
              },
              {
                title: "Instant Performance Tracking",
                description: "Real-time win rate, profit factor, average R:R, and drawdown metrics—updated with every trade.",
                icon: ChartLineUp
              },
              {
                title: "Consistent Documentation",
                description: "Every trade is logged regardless of outcome. No emotional bias affecting what gets recorded.",
                icon: CheckCircle
              },
              {
                title: "Pattern Recognition",
                description: "Automated analysis reveals which setups actually work, which timeframes suit you, and which behaviors hurt your results.",
                icon: Brain
              }
            ].map((item, index) => (
              <div 
                key={index}
                className="flex gap-4 items-start bg-[#121212] border border-white/10 rounded-lg p-6 hover:border-[#00E676]/30 transition-colors"
              >
                <div className="w-12 h-12 rounded-lg bg-[#00E676]/10 flex items-center justify-center flex-shrink-0">
                  <item.icon size={24} weight="duotone" className="text-[#00E676]" />
                </div>
                <div>
                  <h3 className="font-semibold mb-1">{item.title}</h3>
                  <p className="text-white/60 text-sm leading-relaxed">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Key Features Section */}
        <section className="mb-16">
          <h2 className="text-2xl md:text-3xl font-bold mb-4">
            Key Features of a Modern Trading Journal
          </h2>
          <p className="text-white/70 mb-8 leading-relaxed">
            Not all trading journals are created equal. Here's what to look for in a 
            quality <strong className="text-white">automated trading journal</strong> in 2026:
          </p>
          
          <div className="bg-[#121212] border border-white/10 rounded-lg overflow-hidden">
            <div className="grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-white/10">
              {[
                { feature: "Chart image capture", description: "Attach screenshots directly to trades" },
                { feature: "Setup categorization", description: "Tag and filter trades by strategy type" },
                { feature: "Partial execution tracking", description: "Log scaled entries and exits accurately" },
                { feature: "Performance analytics", description: "Win rate, profit factor, R:R by setup" },
                { feature: "Calendar visualization", description: "See trading patterns over time" },
                { feature: "Multi-asset support", description: "Crypto, forex, stocks in one place" }
              ].map((item, index) => (
                <div key={index} className="p-5 hover:bg-white/5 transition-colors">
                  <div className="flex items-center gap-2 mb-1">
                    <CheckCircle size={16} weight="fill" className="text-[#00E676]" />
                    <span className="font-medium text-sm">{item.feature}</span>
                  </div>
                  <p className="text-white/50 text-xs pl-6">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Comparison Section */}
        <section className="mb-16">
          <h2 className="text-2xl md:text-3xl font-bold mb-8">
            Manual vs Automated Journal: The Comparison
          </h2>
          
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left py-4 px-4 text-white/60 font-medium">Aspect</th>
                  <th className="text-left py-4 px-4 text-red-400 font-medium">Manual Journal</th>
                  <th className="text-left py-4 px-4 text-[#00E676] font-medium">Automated Journal</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {[
                  { aspect: "Time per trade", manual: "5-10 minutes", automated: "< 30 seconds" },
                  { aspect: "Data accuracy", manual: "Prone to errors", automated: "100% accurate" },
                  { aspect: "Consistency", manual: "Varies by mood", automated: "Every trade logged" },
                  { aspect: "Analytics", manual: "Manual calculations", automated: "Real-time insights" },
                  { aspect: "Pattern detection", manual: "Nearly impossible", automated: "Automatic" },
                  { aspect: "Historical review", manual: "Scroll through notes", automated: "Searchable & filtered" }
                ].map((row, index) => (
                  <tr key={index} className="hover:bg-white/5 transition-colors">
                    <td className="py-4 px-4 font-medium">{row.aspect}</td>
                    <td className="py-4 px-4 text-white/60">{row.manual}</td>
                    <td className="py-4 px-4 text-white/80">{row.automated}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Why Now Section */}
        <section className="mb-16">
          <h2 className="text-2xl md:text-3xl font-bold mb-6">
            Why 2026 Is the Year to Switch
          </h2>
          <div className="prose prose-invert max-w-none">
            <p className="text-white/70 leading-relaxed mb-4">
              Markets are faster and more competitive than ever. The traders who succeed aren't 
              necessarily the smartest—they're the ones who learn from their data fastest. 
              An <strong className="text-white">automated trading journal</strong> isn't a luxury 
              anymore; it's the baseline for serious traders.
            </p>
            <p className="text-white/70 leading-relaxed">
              The cost of not tracking properly? Repeating the same mistakes, never identifying 
              your edge, and leaving money on the table month after month. The traders using 
              automation aren't guessing about what works—they know. If you're wondering how to 
              get started, learn <Link to="/how-to-analyze-your-trades" className="text-[#00E676] hover:underline">how to analyze your trades</Link> effectively.
            </p>
          </div>
        </section>

        {/* Related Articles Section */}
        <section className="mb-16">
          <h2 className="text-xl font-semibold mb-6">Related Articles</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <Link to="/manual-vs-automated-trading-journal" className="bg-[#121212] border border-white/10 rounded-lg p-5 hover:border-[#00E676]/30 transition-colors group">
              <h3 className="font-medium mb-2 group-hover:text-[#00E676] transition-colors">Manual vs Automated Trading Journal</h3>
              <p className="text-white/50 text-sm">Compare both approaches and find what works best for you.</p>
            </Link>
            <Link to="/crypto-trading-journal" className="bg-[#121212] border border-white/10 rounded-lg p-5 hover:border-[#00E676]/30 transition-colors group">
              <h3 className="font-medium mb-2 group-hover:text-[#00E676] transition-colors">Best Crypto Trading Journal</h3>
              <p className="text-white/50 text-sm">Specific strategies for tracking crypto trades effectively.</p>
            </Link>
            <Link to="/how-to-analyze-your-trades" className="bg-[#121212] border border-white/10 rounded-lg p-5 hover:border-[#00E676]/30 transition-colors group">
              <h3 className="font-medium mb-2 group-hover:text-[#00E676] transition-colors">How to Analyze Your Trades</h3>
              <p className="text-white/50 text-sm">Step-by-step guide to reviewing trades like a pro.</p>
            </Link>
            <Link to="/why-traders-fail-to-improve" className="bg-[#121212] border border-white/10 rounded-lg p-5 hover:border-[#00E676]/30 transition-colors group">
              <h3 className="font-medium mb-2 group-hover:text-[#00E676] transition-colors">Why Traders Fail to Improve</h3>
              <p className="text-white/50 text-sm">Common mistakes that keep traders stuck in place.</p>
            </Link>
          </div>
        </section>

        {/* CTA Section */}
        <section className="mb-8">
          <div className="bg-gradient-to-br from-[#00E676]/10 via-[#121212] to-purple-500/10 border border-[#00E676]/30 rounded-lg p-8 md:p-12 text-center">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#00E676]/20 flex items-center justify-center">
              <TrendUp size={32} weight="duotone" className="text-[#00E676]" />
            </div>
            
            <h2 className="text-2xl md:text-3xl font-bold mb-4">
              Ready to Automate Your Trading Journal?
            </h2>
            
            <p className="text-white/70 max-w-2xl mx-auto mb-8 leading-relaxed">
              <strong className="text-white">GENI</strong> is a modern automated trading journal 
              designed for traders who want clarity and performance insights. Paste your chart, 
              get instant analysis, and finally understand what's working in your trading.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/signup"
                className="inline-flex items-center justify-center gap-2 px-8 py-3 bg-[#00E676] text-black font-semibold rounded-sm hover:bg-[#00E676]/90 transition-colors"
              >
                Start Free
                <ArrowRight size={18} weight="bold" />
              </Link>
              <Link
                to="/"
                className="inline-flex items-center justify-center gap-2 px-8 py-3 bg-white/10 text-white font-semibold rounded-sm hover:bg-white/20 transition-colors"
              >
                Learn More About GENI
              </Link>
            </div>
            
            <p className="text-white/40 text-xs mt-6">
              No credit card required · Free tier available
            </p>
          </div>
        </section>

        {/* Schema Markup (JSON-LD) */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Article",
              "headline": "What Is an Automated Trading Journal? (And Why You Need One in 2026)",
              "description": "Learn what an automated trading journal is and why every trader needs one. Discover how automation eliminates manual tracking errors and improves trading performance.",
              "author": {
                "@type": "Organization",
                "name": "GENI"
              },
              "publisher": {
                "@type": "Organization",
                "name": "GENI",
                "logo": {
                  "@type": "ImageObject",
                  "url": "https://genitradeapp.com/geni-logo.png"
                }
              },
              "datePublished": "2026-01-01",
              "dateModified": new Date().toISOString().split('T')[0]
            })
          }}
        />
      </main>

      {/* Footer */}
      <footer className="border-t border-white/10 py-8">
        <div className="max-w-4xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <Link to="/" className="flex items-center">
            <img 
              src={LOGO_HORIZONTAL}
              alt="GENI" 
              className="h-6 opacity-60"
            />
          </Link>
          <div className="flex items-center gap-6 text-sm text-white/40">
            <Link to="/privacy" className="hover:text-white/60 transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-white/60 transition-colors">Terms</Link>
            <span>© 2026 GENI</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AutomatedTradingJournal;
